import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
	inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ICON } from '@core/util/icon';
import {
	BusinessChecklistVM,
	ChecklistStageVM,
	ChecklistTaskVM,
} from '@models/checklist';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { AddTemplateDialogComponent } from '../add-template-dialog/add-template-dialog.component';
import {
	BusinessChecklistsDispatch,
	BusinessChecklistsSelectors,
} from '@state/business/checklists';
import { nanoid } from 'nanoid/non-secure';
import { SnackbarService } from '@services/ui/snackbar.service';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { MatterFileType } from '@models/matter';
import { BusinessAddress } from '@models/businesses';
import { BusinessAddressesState } from '@state/business/address';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { Store } from '@ngxs/store';
import { map, filter, take } from 'rxjs';

enum KebabOptions {
	SET_AS_DEFAULT = 'setAsDefault',
	DUPLICATE = 'duplicate',
	EDIT = 'edit',
	DELETE = 'delete',
}

@Component({
	selector: 'app-checklist-container',
	templateUrl: './checklist-container.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ChecklistContainerComponent implements OnInit {
	@Input() checklist: BusinessChecklistVM;

	private store = inject(Store);

	public kebabOptions: DropdownOption<KebabOptions>[] = [
		{ name: 'Set as default', value: KebabOptions.SET_AS_DEFAULT },
		{ name: 'Duplicate', value: KebabOptions.DUPLICATE },
	];

	public ICON = ICON;
	public officeName: string;

	@SelectSnapshot(BusinessChecklistsSelectors.getDefaultChecklistSaleVM)
	defaultChecklistSale: BusinessChecklistVM;
	@SelectSnapshot(BusinessChecklistsSelectors.getDefaultChecklistPurchaseVM)
	defaultChecklistPurchase: BusinessChecklistVM;
	@SelectSnapshot(BusinessChecklistsSelectors.getDefaultChecklistRefinanceVM)
	defaultChecklistRefinance: BusinessChecklistVM;
	@SelectSnapshot(BusinessAddressesState.getPrimaryAddress)
	primaryAddress: BusinessAddress;

	private sortedChecklists$ = this.store.select(
		BusinessChecklistsSelectors.getSortedChecklists,
	);

	constructor(
		private dialogRef: MatDialog,
		private checklistDispatch: BusinessChecklistsDispatch,
		private snackbarService: SnackbarService,
		private analytics: AnalyticsService,
	) {}

	ngOnInit(): void {
		if (!this.checklist.locked) {
			this.kebabOptions = [
				{ name: 'Set as default', value: KebabOptions.SET_AS_DEFAULT },
				{ name: 'Duplicate', value: KebabOptions.DUPLICATE, icon: ICON.COPY },
				{ name: 'Edit', value: KebabOptions.EDIT, icon: ICON.EDIT },
				{
					name: 'Delete',
					value: KebabOptions.DELETE,
					icon: ICON.DELETE,
					classes: ['text-red'],
				},
			];
		}
	}

	onKebabOptionSelected(option: DropdownResult<DropdownOption<KebabOptions>>) {
		switch (option.result.value) {
			case KebabOptions.SET_AS_DEFAULT:
				this.setAsDefault();
				break;
			case KebabOptions.DUPLICATE:
				this.duplicateChecklist();
				break;
			case KebabOptions.EDIT:
				this.openChecklist(true);
				break;
			case KebabOptions.DELETE:
				this.deleteTemplate();
				break;
		}
	}

	setAsDefault(): void {
		if (this.checklist.default) {
			return;
		}

		if (!this.checklist.locked) {
			this.checklistDispatch.SetDefault(this.checklist.id);
		}

		switch (this.checklist.fileType) {
			case MatterFileType.SALE:
				if (!this.defaultChecklistSale?.locked) {
					this.checklistDispatch.Create({
						...this.defaultChecklistSale,
						default: false,
					});
				}
				break;
			case MatterFileType.PURCHASE:
				if (!this.defaultChecklistPurchase?.locked) {
					this.checklistDispatch.Create({
						...this.defaultChecklistPurchase,
						default: false,
					});
				}
				break;
			case MatterFileType.REFINANCE:
				if (!this.defaultChecklistRefinance?.locked) {
					this.checklistDispatch.Create({
						...this.defaultChecklistRefinance,
						default: false,
					});
				}
				break;
		}
	}

	openChecklist(editing?: boolean, checklist?: BusinessChecklistVM): void {
		this.dialogRef.open(AddTemplateDialogComponent, {
			maxWidth: '90vw',
			data: {
				selectedChecklist: checklist || this.checklist,
				editing: editing,
			},
		});
	}

	onKebabClick(evt: Event): void {
		evt.stopImmediatePropagation();
	}

	duplicateChecklist(): void {
		if (this.checklist.locked) {
			this.analytics.logEvent(AnalyticsEvent.CHECKLIST_TEMPLATE_DUPLICATE);
		}

		const newChecklistStages: ChecklistStageVM[] = this.checklist.stages.map(
			(stage) => {
				const tasks: ChecklistTaskVM[] = stage.tasks.map((task) => {
					return {
						...task,
						id: nanoid(8),
					};
				});

				return {
					...stage,
					id: nanoid(8),
					tasks,
				};
			},
		);

		const newChecklist: BusinessChecklistVM = {
			...this.checklist,
			id: nanoid(8),
			draft: true,
			default: false,
			locked: false,
			title: `${this.checklist.title} (copy)`,
			stages: newChecklistStages,
			created: new Date().toISOString(),
			lastEdited: new Date().toISOString(),
		};

		this.checklistDispatch.Create(newChecklist);

		const newChecklistId = newChecklist.id;

		this.sortedChecklists$
			.pipe(
				map((checklists) => checklists.find((c) => c.id === newChecklistId)),
				filter((targetChecklist) => !!targetChecklist),
				take(1),
			)
			.subscribe((targetChecklist) => {
				this.openChecklist(true, targetChecklist);

				this.snackbarService.openSnackBar({
					text: 'Checklist template successfully duplicated. Ready to edit!',
					type: 'success',
				});
			});
	}

	deleteTemplate(): void {
		this.checklistDispatch.Remove(this.checklist.id);
		this.snackbarService.openSnackBar({
			text: 'Checklist template successfully deleted.',
			type: 'success',
		});
	}
}
